import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { gwtStyles } from '../../config/theme';
import { useUIStore } from '../../stores/uiStore';
import type { WhereBean } from '../../api/types';

interface AdvancedSearchDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Advanced Search dialog — mirrors AdvancedSearchDialog from GWT.
 *
 * A form with fields for searching studies by various criteria.
 * Results open in a new STUDYSEARCH tab (same as GWT behavior).
 */
export const AdvancedSearchDialog = ({ open, onClose }: AdvancedSearchDialogProps) => {
  const setAdvancedFilters = useUIStore((s) => s.setAdvancedFilters);
  const setQuickSearch = useUIStore((s) => s.setQuickSearch);

  // Form state
  const [patientName, setPatientName] = useState('');
  const [patientId, setPatientId] = useState('');
  const [accessionNumber, setAccessionNumber] = useState('');
  const [modality, setModality] = useState('');
  const [bodyPart, setBodyPart] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [physician, setPhysician] = useState('');
  const [radiologist, setRadiologist] = useState('');
  const [description, setDescription] = useState('');
  const [site, setSite] = useState('');
  const [urgentOnly, setUrgentOnly] = useState(false);

  const handleSearch = useCallback(() => {
    // Build proper WhereBean[] for the backend's makeWhere()
    const clauses: WhereBean[] = [];
    if (patientName) clauses.push({ attr: 'name', op: 'LIKE', sval: [`%${patientName}%`] });
    if (patientId) clauses.push({ attr: 'id', op: 'EQUALS', sval: [patientId] });
    if (accessionNumber) clauses.push({ attr: 'acc', op: 'EQUALS', sval: [accessionNumber] });
    if (modality) clauses.push({ attr: 'mod', op: 'EQUALS', sval: [modality] });
    if (bodyPart) clauses.push({ attr: 'bod', op: 'LIKE', sval: [`%${bodyPart}%`] });
    if (status) clauses.push({ attr: 'status', op: 'EQUALS', sval: [status] });
    if (dateFrom) {
      // Convert from YYYY-MM-DD to yyyy/MM/dd HH:mm format
      const formatted = dateFrom.replace(/-/g, '/') + ' 00:00';
      clauses.push({ attr: 'date', op: 'GREATERTHANEQUALS', dval: [formatted] });
    }
    if (dateTo) {
      const formatted = dateTo.replace(/-/g, '/') + ' 23:59';
      clauses.push({ attr: 'date', op: 'LESSTHANEQUALS', dval: [formatted] });
    }
    if (physician) clauses.push({ attr: 'physician', op: 'LIKE', sval: [`%${physician}%`] });
    if (radiologist) clauses.push({ attr: 'radiologist', op: 'LIKE', sval: [`%${radiologist}%`] });
    if (description) clauses.push({ attr: 'descr', op: 'LIKE', sval: [`%${description}%`] });
    if (site) clauses.push({ attr: 'siteName', op: 'LIKE', sval: [`%${site}%`] });
    if (urgentOnly) clauses.push({ attr: 'urgentActionNeeded', op: 'EQUALS', sval: ['true'] });

    if (clauses.length > 0) {
      // Clear quick search when using advanced filters
      setQuickSearch('', 'smart');
      setAdvancedFilters(clauses);
    }

    onClose();
  }, [
    patientName, patientId, accessionNumber, modality, bodyPart,
    status, dateFrom, dateTo, physician, radiologist, description,
    site, urgentOnly, setAdvancedFilters, setQuickSearch, onClose,
  ]);

  const handleClear = useCallback(() => {
    setPatientName('');
    setPatientId('');
    setAccessionNumber('');
    setModality('');
    setBodyPart('');
    setStatus('');
    setDateFrom('');
    setDateTo('');
    setPhysician('');
    setRadiologist('');
    setDescription('');
    setSite('');
    setUrgentOnly(false);
    // Also clear active advanced filters
    setAdvancedFilters([]);
  }, [setAdvancedFilters]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 600,
          maxHeight: '85vh',
          borderRadius: '6px',
          border: `1px solid ${gwtStyles.borderSubtle}`,
          backgroundColor: gwtStyles.popupBg,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: 14,
          fontWeight: 600,
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: gwtStyles.wizardDetail,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        Advanced Search
        <IconButton size="small" onClick={onClose} sx={{ width: 24, height: 24 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 2 }}>
        {/* Patient info */}
        <SectionLabel>Patient Information</SectionLabel>
        <FieldRow label="Patient Name">
          <TextField
            size="small" fullWidth value={patientName}
            onChange={(e) => setPatientName(e.target.value)}
            placeholder="Last, First"
            sx={fieldSx}
          />
        </FieldRow>
        <FieldRow label="Patient ID">
          <TextField
            size="small" fullWidth value={patientId}
            onChange={(e) => setPatientId(e.target.value)}
            sx={fieldSx}
          />
        </FieldRow>

        <Divider sx={{ my: 1.5 }} />

        {/* Study info */}
        <SectionLabel>Study Criteria</SectionLabel>
        <FieldRow label="Accession #">
          <TextField
            size="small" fullWidth value={accessionNumber}
            onChange={(e) => setAccessionNumber(e.target.value)}
            sx={fieldSx}
          />
        </FieldRow>
        <FieldRow label="Modality">
          <TextField
            size="small" fullWidth value={modality}
            onChange={(e) => setModality(e.target.value)}
            placeholder="e.g. CT, MR, XR"
            sx={fieldSx}
          />
        </FieldRow>
        <FieldRow label="Body Part">
          <TextField
            size="small" fullWidth value={bodyPart}
            onChange={(e) => setBodyPart(e.target.value)}
            sx={fieldSx}
          />
        </FieldRow>
        <FieldRow label="Description">
          <TextField
            size="small" fullWidth value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={fieldSx}
          />
        </FieldRow>
        <FieldRow label="Status">
          <Select
            size="small" fullWidth value={status}
            onChange={(e) => setStatus(e.target.value)}
            displayEmpty
            sx={{ fontSize: 12 }}
          >
            <MenuItem value="" sx={{ fontSize: 12 }}>Any</MenuItem>
            <MenuItem value="NEWSTUDY" sx={{ fontSize: 12 }}>New Study</MenuItem>
            <MenuItem value="ASSIGNED" sx={{ fontSize: 12 }}>Assigned</MenuItem>
            <MenuItem value="DICTATED" sx={{ fontSize: 12 }}>Dictated</MenuItem>
            <MenuItem value="TRANSCRIBED" sx={{ fontSize: 12 }}>Transcribed</MenuItem>
            <MenuItem value="READ" sx={{ fontSize: 12 }}>Read</MenuItem>
            <MenuItem value="REPORTED" sx={{ fontSize: 12 }}>Reported</MenuItem>
            <MenuItem value="DELIVERED" sx={{ fontSize: 12 }}>Delivered</MenuItem>
            <MenuItem value="ORDERED" sx={{ fontSize: 12 }}>Ordered</MenuItem>
            <MenuItem value="SCHEDULED" sx={{ fontSize: 12 }}>Scheduled</MenuItem>
            <MenuItem value="INPROGRESS" sx={{ fontSize: 12 }}>In Progress</MenuItem>
          </Select>
        </FieldRow>

        <Divider sx={{ my: 1.5 }} />

        {/* Date range */}
        <SectionLabel>Date Range</SectionLabel>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <FieldRow label="From">
            <TextField
              size="small" type="date" fullWidth value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              sx={fieldSx}
            />
          </FieldRow>
          <FieldRow label="To">
            <TextField
              size="small" type="date" fullWidth value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              sx={fieldSx}
            />
          </FieldRow>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Personnel */}
        <SectionLabel>Personnel</SectionLabel>
        <FieldRow label="Physician">
          <TextField
            size="small" fullWidth value={physician}
            onChange={(e) => setPhysician(e.target.value)}
            sx={fieldSx}
          />
        </FieldRow>
        <FieldRow label="Radiologist">
          <TextField
            size="small" fullWidth value={radiologist}
            onChange={(e) => setRadiologist(e.target.value)}
            sx={fieldSx}
          />
        </FieldRow>
        <FieldRow label="Site">
          <TextField
            size="small" fullWidth value={site}
            onChange={(e) => setSite(e.target.value)}
            sx={fieldSx}
          />
        </FieldRow>

        <Divider sx={{ my: 1.5 }} />

        <FormControlLabel
          control={
            <Checkbox size="small" checked={urgentOnly} onChange={(e) => setUrgentOnly(e.target.checked)} />
          }
          label={<Typography variant="body2" sx={{ fontSize: 12 }}>Urgent studies only</Typography>}
        />
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 1.5, justifyContent: 'space-between' }}>
        <Button size="small" onClick={handleClear} sx={{ textTransform: 'none', fontSize: 12 }}>
          Clear All
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: 12 }}>
            Cancel
          </Button>
          <Button
            size="small" variant="contained" onClick={handleSearch}
            sx={{ textTransform: 'none', fontSize: 12 }}
          >
            Search
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

/* ── Sub-components ── */

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12, mb: 0.5, color: gwtStyles.accentPrimary }}>
    {children}
  </Typography>
);

const FieldRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75, gap: 1 }}>
    <Typography variant="body2" sx={{ fontSize: 12, minWidth: 100, textAlign: 'right', flexShrink: 0 }}>
      {label}:
    </Typography>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

const fieldSx = { '& .MuiInputBase-root': { fontSize: 12 } };
